import { Injectable, signal } from '@angular/core';

import { isDarkMapLibreMarkerColor, isLightMapLibreMarkerColor } from '../helpers/maplibre.helper';
import { IMapMarkerTheme } from '../models/i-map-marker-theme';
import { IMapLibreOutletMarkerHtmlOptions } from '../models/i-maplibre-outlet-marker-html-options';

const DARK_GLYPH_COLOR = '#212529';

const EMPTY_MAP_MARKER_THEME: IMapMarkerTheme = {
  iconUrl: null,
  iconWhiteUrl: null,
  bgColor: null,
  selectedBgColor: null,
  fallbackBgColor: null,
  fallbackSelectedBgColor: null,
};

/**
 * Seller-webshop-theme outlet map markers (bakery-page only). Stays empty for
 * marketplace/procurement, which never set it, so markers keep the icon-less
 * stylesheet palette.
 */
@Injectable({ providedIn: 'root' })
export class MapMarkerThemeService {
  private readonly _theme = signal<IMapMarkerTheme>(EMPTY_MAP_MARKER_THEME);
  readonly theme = this._theme.asReadonly();

  setTheme(theme: Partial<IMapMarkerTheme> | null | undefined): void {
    const next = { ...EMPTY_MAP_MARKER_THEME };

    (Object.keys(next) as (keyof IMapMarkerTheme)[]).forEach((key) => next[key] = theme?.[key] || null);

    this._theme.set(next);
  }

  getMarkerOptions(isActive: boolean, isDisabled = false): IMapLibreOutletMarkerHtmlOptions {
    const { iconUrl, iconWhiteUrl, bgColor, selectedBgColor, fallbackBgColor, fallbackSelectedBgColor } = this._theme();
    const themeBackground = isActive ? selectedBgColor : bgColor;
    const background = themeBackground || (isActive ? fallbackSelectedBgColor : fallbackBgColor);
    const useWhiteIcon = Boolean(iconWhiteUrl) && isDarkMapLibreMarkerColor(background);
    const resolvedIconUrl = useWhiteIcon ? iconWhiteUrl : iconUrl;

    return {
      isActive,
      isDisabled,
      iconUrl: resolvedIconUrl,
      bgColor,
      selectedBgColor,
      glyphColor: this._getGlyphColor(resolvedIconUrl, themeBackground),
    };
  }

  /**
   * The built-in glyph is white; only a light theme background needs it dark.
   * Palette backgrounds are left alone — the icon-less palette is inverted
   * (`.pink-circle`), so the fallbacks here don't describe it.
   */
  private _getGlyphColor(iconUrl: string | null, themeBackground: string | null): string | null {
    return !iconUrl && isLightMapLibreMarkerColor(themeBackground) ? DARK_GLYPH_COLOR : null;
  }
}
